import React from 'react';
import { WAVEFORMS, Bar } from '@/data/waveforms';

const X0 = 8; // left padding inside the svg (design px)

/** Reference bar x-positions (48 bars, matching the design) used to lay out waveforms of uploaded tracks. */
const REF_X = WAVEFORMS['electronic-inspiration'].map((b) => b[0]);

/** Resample peaks to `n` values (bucket-averaged when shrinking, interpolated when growing). */
function resample(peaks: number[], n: number): number[] {
  if (!peaks.length) return Array(n).fill(0.3);
  if (peaks.length === n) return peaks;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * peaks.length;
    const b = ((i + 1) / n) * peaks.length;
    if (b - a >= 1) {
      let sum = 0;
      let w = 0;
      for (let j = Math.floor(a); j < Math.ceil(b); j++) {
        const cover = Math.min(b, j + 1) - Math.max(a, j);
        sum += peaks[Math.min(peaks.length - 1, j)] * cover;
        w += cover;
      }
      out.push(sum / (w || 1));
    } else {
      const x = (a + b) / 2 - 0.5;
      const j = Math.max(0, Math.min(peaks.length - 1, Math.floor(x)));
      const k = Math.min(peaks.length - 1, j + 1);
      const t = Math.max(0, Math.min(1, x - j));
      out.push(peaks[j] * (1 - t) + peaks[k] * t);
    }
  }
  return out;
}

/** Build bars from normalised peaks (0..1) computed from an uploaded MP3. */
export function barsFromPeaks(peaks: number[]): Bar[] {
  const vals = resample(peaks, REF_X.length);
  return vals.map((v, i) => {
    const p = Math.min(1, Math.max(0, v));
    const h = Math.max(0.6, p * 15.5);
    return [REF_X[i], -h, h, 0.55 + 0.45 * p] as Bar;
  });
}

/** Pick the bar geometry for a track: uploaded peaks > sampled reference > neutral fallback. */
export function barsFor(slug?: string | null, peaks?: number[] | null, index = 0): Bar[] {
  if (peaks && peaks.length) return barsFromPeaks(peaks);
  if (slug && WAVEFORMS[slug]) return WAVEFORMS[slug];
  const keys = ['asere', 'electronic-inspiration', 'french-kiss'];
  return WAVEFORMS[keys[index % 3]];
}

/** Waveform rendered from bar geometry (sampled from the reference, or computed from the uploaded MP3). */
export default function Waveform({
  id,
  bars,
  active,
  progress,
  onSeek,
  preview,
  onHoverStart,
  onHoverEnd,
}: {
  id: string;
  bars: Bar[];
  active: boolean;
  progress: number;
  onSeek?: (fraction: number) => void;
  /** hover-preview window + playhead, as fractions of the track */
  preview?: { from: number; to: number; at: number } | null;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}) {
  if (!bars.length) return null;
  const first = bars[0][0];
  const last = bars[bars.length - 1][0];
  const playedUntil = first + (last - first) * progress;
  const px = (f: number) => first + (last - first) * f;
  const pv = preview ? { from: px(preview.from), to: px(preview.to), at: px(preview.at) } : null;

  return (
    <svg
      className={`ek-wave ${pv ? 'is-previewing' : ''}`}
      viewBox="0 0 180 50"
      width="180"
      height="50"
      role="img"
      aria-label="Waveform"
      data-testid={`waveform-${id}`}
      data-preview={pv ? 'on' : 'off'}
      onPointerEnter={(e) => e.pointerType === 'mouse' && onHoverStart?.()}
      onPointerLeave={(e) => e.pointerType === 'mouse' && onHoverEnd?.()}
      onClick={(e) => {
        if (!onSeek) return;
        const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
        const fx = ((e.clientX - r.left) / r.width) * 180 + X0;
        onSeek(Math.max(0, Math.min(1, (fx - first) / (last - first))));
      }}
    >
      {bars.map(([x, t, b, k], i) => {
        const played = active && x <= playedUntil + 0.5;
        let cls = active ? (played ? 'ek-bar-played' : 'ek-bar-rest') : 'ek-bar-idle';
        if (pv && x >= pv.from - 0.5 && x <= pv.to + 0.5) cls = x <= pv.at + 0.5 ? 'ek-bar-preview' : 'ek-bar-window';
        return (
          <rect
            key={i}
            className={cls}
            x={x - X0 + 0.2}
            y={25 + t - 0.5}
            width={1.6}
            height={Math.max(1.2, b - t + 1)}
            rx={0.8}
            style={{ opacity: 0.35 + 0.65 * k }}
          />
        );
      })}
    </svg>
  );
}
